import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Calendar, AlertCircle, Cloud, ChevronDown, ChevronRight, Search } from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PuntoVendita } from '../types';
import {
  PageContainer,
  PageHeader,
  Card,
  CardBody,
  FormRow,
  FormGroup,
  Label,
  Input,
  Select,
  Badge,
  useToast
} from '../components/ui';

const FiltersCard = styled(Card)`
  margin-bottom: var(--space-xl);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: var(--space-md);
    text-align: left;
    border-bottom: 1px solid var(--gray-100);
  }

  th {
    background: var(--gray-50);
    font-weight: var(--font-weight-semibold);
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
  }

  td {
    color: var(--text-primary);
    vertical-align: top;
  }

  tbody tr:hover {
    background: var(--gray-50);
  }
`;

const NoteBox = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  max-width: 300px;
  line-height: 1.4;
`;

interface RecordAnalisi {
  data: string;
  previsto: number | null;
  prodotto: number;
  giacenza: number | null;
  venduto: number | null;
  incasso: number | null;
  meteo: string;
  note_ai: string;
  note_produzione: string;
  status_venduto: string;
  gusti_previsti: Array<{
    gusto_id: number;
    nome: string;
    kg_suggeriti: number;
    motivazione?: string;
  }>;
  gusti_prodotti: Array<{
    gusto_id: number;
    nome: string;
    quantita_kg: number;
  }>;
  gusti_giacenza_precedente?: Array<{
    gusto_id: number;
    nome: string;
    quantita_kg: number;
  }>;
}

const ClickableRow = styled.tr<{ isExpanded?: boolean }>`
  cursor: pointer;
  background: ${props => props.isExpanded ? 'var(--gray-50)' : 'transparent'};

  &:hover {
    background: var(--gray-50) !important;
  }
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--gray-100);

  th, td {
    padding: var(--space-sm) var(--space-md);
    font-size: var(--font-size-sm);
    border-bottom: 1px solid var(--gray-100);
  }

  th {
    background: var(--gray-50);
    font-weight: var(--font-weight-semibold);
    color: var(--text-secondary);
    text-align: left;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover {
    background: var(--gray-50);
  }
`;

const AnalisiPrevisioniPage: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [selectedPv, setSelectedPv] = useState<number | ''>('');

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});

  const toggleRow = (dateStr: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [dateStr]: !prev[dateStr]
    }));
  };

  const [data, setData] = useState<RecordAnalisi[]>([]);
  const [loading, setLoading] = useState(true);

  // Carica i punti vendita all'avvio
  useEffect(() => {
    const loadPuntiVendita = async () => {
      try {
        const response = await apiService.getPuntiVendita();
        setPuntiVendita(response);
        if (response.length > 0) {
          const userPvId = user?.punto_vendita_id;
          const initialPv = userPvId && response.some(pv => pv.id === userPvId)
            ? userPvId
            : response[0].id;
          setSelectedPv(initialPv);
        }
      } catch (err) {
        console.error('Errore caricamento punti vendita:', err);
      }
    };
    loadPuntiVendita();
  }, [user]);

  // Carica i dati quando cambiano filtri
  useEffect(() => {
    if (selectedPv) {
      fetchDati(dateFrom, dateTo, Number(selectedPv));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio filtri; fetchDati è ricreata ad ogni render
  }, [selectedPv, dateFrom, dateTo]);

  const fetchDati = async (start: string, end: string, pvId: number) => {
    try {
      setLoading(true);
      const json = await apiService.getAnalisiGiornaliera(start, end, pvId);
      setData(json);
    } catch (err) {
      console.error(err);
      toast.error('Impossibile caricare i dati di analisi.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    }).format(d);
  };

  const getDiffBadge = (real: number | null, forecast: number | null) => {
    if (real === null || forecast === null) return null;
    const diff = real - forecast;
    const percentage = forecast > 0 ? (diff / forecast) * 100 : 0;

    if (Math.abs(percentage) <= 10) return <Badge $variant="success">In target ({diff > 0 ? '+' : ''}{percentage.toFixed(0)}%)</Badge>;
    if (percentage > 10) return <Badge $variant="warning">Sopra ({percentage.toFixed(0)}%)</Badge>;
    return <Badge $variant="error">Sotto ({percentage.toFixed(0)}%)</Badge>;
  };

  const getAccuracyBadge = (venduto: number | null, previsto: number | null) => {
    if (venduto === null || previsto === null || previsto === 0) return null;
    const diff = venduto - previsto;
    const percentage = (diff / previsto) * 100;

    if (Math.abs(percentage) <= 10) {
      return <Badge $variant="success" style={{ marginTop: '4px' }}>AI Precisa ({diff > 0 ? '+' : ''}{percentage.toFixed(0)}%)</Badge>;
    }
    if (diff > 0) {
      return <Badge $variant="error" style={{ marginTop: '4px' }}>AI Sottostima ({percentage.toFixed(0)}%)</Badge>;
    }
    return <Badge $variant="warning" style={{ marginTop: '4px' }}>AI Sovrastima ({percentage.toFixed(0)}%)</Badge>;
  };

  const getProductionBalanceBadge = (prodotto: number, venduto: number | null) => {
    if (venduto === null) return null;
    const diff = prodotto - venduto;

    if (Math.abs(diff) < 0.1) {
      return <Badge $variant="success" style={{ marginTop: '4px' }}>Bilanciato</Badge>;
    }
    if (diff > 0) {
      return <Badge $variant="neutral" style={{ marginTop: '4px' }}>Surplus (+{diff.toFixed(1)} kg)</Badge>;
    }
    return <Badge $variant="info" style={{ marginTop: '4px' }}>Uso Stock ({diff.toFixed(1)} kg)</Badge>;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Analisi Previsioni vs. Reale"
        icon={<Calendar />}
      />

      <FiltersCard>
        <CardBody>
          <FormRow>
            <FormGroup>
              <Label>Punto Vendita</Label>
              <Select
                value={selectedPv}
                onChange={(e) => setSelectedPv(e.target.value === '' ? '' : parseInt(e.target.value))}
              >
                <option value="">Seleziona punto vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>{pv.nome}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Dal</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Al</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </FormGroup>
          </FormRow>
        </CardBody>
      </FiltersCard>

      <Card>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Caricamento dati in corso...</div>
        ) : data.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Nessun dato disponibile.</div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Meteo Previsto</th>
                <th>AI Previsto</th>
                <th>Prodotto</th>
                <th>Giacenza</th>
                <th>Venduto</th>
                <th>Incasso</th>
                <th>Note Produzione / AI</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const isExpanded = !!expandedDates[row.data];

                // Unione di tutti i gusti presenti nelle tre liste
                const allFlavors = Array.from(
                  new Set([
                    ...(row.gusti_previsti || []).map((g: any) => g.nome),
                    ...(row.gusti_prodotti || []).map((g: any) => g.nome),
                    ...(row.gusti_giacenza_precedente || []).map((g: any) => g.nome)
                  ])
                ).sort((a, b) => a.localeCompare(b));

                const flavorComparisons = allFlavors.map(name => {
                  const prev = (row.gusti_previsti || []).find((g: any) => g.nome === name);
                  const prod = (row.gusti_prodotti || []).find((g: any) => g.nome === name);
                  const giac = (row.gusti_giacenza_precedente || []).find((g: any) => g.nome === name);
                  return {
                    nome: name,
                    giacenzaPrecedente: giac ? parseFloat(giac.quantita_kg as any) : 0,
                    previsto: prev ? parseFloat(prev.kg_suggeriti as any) : 0,
                    prodotto: prod ? parseFloat(prod.quantita_kg as any) : 0
                  };
                }).filter(c => c.previsto > 0 || c.prodotto > 0 || c.giacenzaPrecedente > 0); // Nascondi se tutti sono 0

                return (
                  <React.Fragment key={idx}>
                    <ClickableRow onClick={() => toggleRow(row.data)} isExpanded={isExpanded}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        <span style={{ marginRight: '8px', color: 'var(--gray-400)', display: 'inline-flex', verticalAlign: 'middle' }}>
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                        {formatDate(row.data)}
                      </td>
                      <td>
                        {row.meteo !== 'ND' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Cloud size={16} />
                            {row.meteo}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {row.previsto !== null ? (
                          <strong style={{ color: 'var(--primary-700)', fontSize: '1.1rem' }}>{row.previsto.toFixed(1)} kg</strong>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>N/D</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <strong style={{ fontSize: '1.1rem' }}>{row.prodotto.toFixed(1)} kg</strong>
                          {getDiffBadge(row.prodotto, row.previsto)}
                        </div>
                      </td>
                      <td>
                        {row.giacenza !== null ? (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 500 }}>
                            {row.giacenza.toFixed(1)} kg
                          </span>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>N/D</span>
                        )}
                      </td>
                      <td>
                        {row.status_venduto ? (
                          <Badge $variant="warning">
                            <AlertCircle size={14} /> {row.status_venduto}
                          </Badge>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong style={{ color: 'var(--success)', fontSize: '1.1rem' }}>
                              {row.venduto?.toFixed(1)} kg
                            </strong>
                            {getAccuracyBadge(row.venduto, row.previsto)}
                            {getProductionBalanceBadge(row.prodotto, row.venduto)}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.incasso !== null ? (
                          <strong style={{ color: 'var(--accent-600)', fontSize: '1.1rem' }}>
                            € {row.incasso.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>N/D</span>
                        )}
                      </td>
                      <td>
                        <NoteBox>
                          {row.note_produzione && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong style={{ color: 'var(--text-secondary)' }}>Operatore:</strong> {row.note_produzione}
                            </div>
                          )}
                          {row.note_ai && (
                            <div style={{ fontStyle: 'italic' }}>
                              <strong style={{ color: 'var(--primary-700)' }}>AI:</strong> {row.note_ai}
                            </div>
                          )}
                        </NoteBox>
                      </td>
                    </ClickableRow>
                    {isExpanded && (
                      <tr style={{ background: 'var(--gray-50)' }}>
                        <td colSpan={8} style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-100)' }}>
                          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Search size={16} /> Dettaglio Gusti Previsti vs. Prodotti
                            </div>
                            {flavorComparisons.length === 0 ? (
                              <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', padding: '1rem', background: 'var(--card-bg)', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--gray-200)' }}>
                                Nessun dettaglio disponibile per questa giornata.
                              </div>
                            ) : (
                              <DetailTable>
                                <thead>
                                  <tr>
                                    <th>Gusto</th>
                                    <th style={{ textAlign: 'right', width: '150px' }}>Giac. Sera Prec.</th>
                                    <th style={{ textAlign: 'right', width: '150px' }}>Previsto AI</th>
                                    <th style={{ textAlign: 'right', width: '150px' }}>Prodotto Reale</th>
                                    <th style={{ textAlign: 'right', width: '150px' }}>Scostamento</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {flavorComparisons.map((c, i) => {
                                    const diff = c.prodotto - c.previsto;
                                    let diffColor = 'var(--success)'; // in target
                                    let diffText = 'In target';
                                    if (diff > 0) {
                                      diffColor = 'var(--warning)'; // produzione in surplus
                                      diffText = `+${diff.toFixed(1)} kg`;
                                    } else if (diff < 0) {
                                      diffColor = 'var(--error)'; // sotto-produzione
                                      diffText = `${diff.toFixed(1)} kg`;
                                    }

                                    return (
                                      <tr key={i}>
                                        <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{c.nome}</td>
                                        <td style={{ textAlign: 'right', fontWeight: c.giacenzaPrecedente > 0 ? 600 : 400, color: c.giacenzaPrecedente > 0 ? 'var(--text-secondary)' : 'var(--gray-400)' }}>
                                          {c.giacenzaPrecedente > 0 ? `${c.giacenzaPrecedente.toFixed(1)} kg` : '0.0 kg'}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: c.previsto > 0 ? 600 : 400, color: c.previsto > 0 ? 'var(--primary-700)' : 'var(--gray-400)' }}>
                                          {c.previsto > 0 ? `${c.previsto.toFixed(1)} kg` : '0.0 kg'}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: c.prodotto > 0 ? 600 : 400, color: c.prodotto > 0 ? 'var(--success)' : 'var(--gray-400)' }}>
                                          {c.prodotto > 0 ? `${c.prodotto.toFixed(1)} kg` : '0.0 kg'}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600, color: diffColor }}>
                                          {diffText}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </DetailTable>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </PageContainer>
  );
};

export default AnalisiPrevisioniPage;
